import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Eye, Pencil, Printer, Plus } from "lucide-react";

import "./JobsPage.css";

const PAGE_SIZE = 10;
const API_BASE_URL = process.env.REACT_APP_BACKEND_API_URL;

// Helper to format date as "Jan 20, 2026"
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const options = { year: "numeric", month: "short", day: "numeric" };
  return new Date(dateStr).toLocaleDateString(undefined, options);
};

export default function JobsPage() {
  const navigate = useNavigate();

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    title: "",
    company: "",
    location: "",
    type: "",
    datePosted: "",
    deadline: "",
  });

  // Fetch jobs from backend
  const fetchJobs = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE_URL}/jobs`);
      setJobs(res.data.jobs || []); // ✅ Use only the jobs array
    } catch (err) {
      console.error("Failed to fetch jobs:", err.message);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  // Filter + Search logic
  const filteredJobs = useMemo(() => {
    if (!Array.isArray(jobs)) return [];
    return jobs.filter((job) => {
      const matchesFilters =
        (!filters.title || job.title.toLowerCase().includes(filters.title.toLowerCase())) &&
        (!filters.company || job.company.toLowerCase().includes(filters.company.toLowerCase())) &&
        (!filters.location || job.location.toLowerCase().includes(filters.location.toLowerCase())) &&
        (!filters.type || job.type.toLowerCase() === filters.type.toLowerCase()) &&
        (!filters.datePosted ||
          new Date(job.datePosted).toDateString() === new Date(filters.datePosted).toDateString()) &&
        (!filters.deadline ||
          new Date(job.deadline).toDateString() === new Date(filters.deadline).toDateString());
      return matchesFilters;
    });
  }, [jobs, filters]);

  const totalPages = Math.ceil(filteredJobs.length / PAGE_SIZE) || 1;
  const paginatedJobs = filteredJobs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="jobs-page">
      {/* PAGE TITLE */}
      <h1 className="jobs-page-title">Jobs Management</h1>

      {/* ACTION BUTTONS */}
      <div className="jobs-actions-row">
        <motion.button
          className="jobs-btn-action"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate("/admin-panel/jobs/post-job")}
        >
          <Plus size={20} style={{ marginRight: "8px" }} /> Post Job
        </motion.button>

        <motion.button
          className="jobs-btn-action"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => window.print()}
        >
          <Printer size={20} style={{ marginRight: "8px" }} /> Print
        </motion.button>
      </div>

      {/* FILTERS */}
      <div className="jobs-filters-row">
        <input
          type="text"
          placeholder="Filter by Title"
          value={filters.title}
          onChange={(e) => setFilters({ ...filters, title: e.target.value })}
        />
        <input
          type="text"
          placeholder="Filter by Company"
          value={filters.company}
          onChange={(e) => setFilters({ ...filters, company: e.target.value })}
        />
        <input
          type="text"
          placeholder="Filter by Location"
          value={filters.location}
          onChange={(e) => setFilters({ ...filters, location: e.target.value })}
        />
        <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}>
          <option value="">All Types</option>
          <option value="Full-time Job">Full-time Job</option>
          <option value="Internship">Internship</option>
          <option value="Contract">Contract</option>
        </select>
        <input
          type="date"
          placeholder="Select posted date"
          value={filters.datePosted}
          onChange={(e) => setFilters({ ...filters, datePosted: e.target.value })}
        />
        <input
          type="date"
          placeholder="Select deadline"
          value={filters.deadline}
          onChange={(e) => setFilters({ ...filters, deadline: e.target.value })}
        />
      </div>

      {/* JOBS TABLE */}
      <div className="jobs-table-card">
        {loading ? (
          <div className="jobs-loading">Loading job postings…</div>
        ) : (
          <>
            <table className="jobs-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Company</th>
                  <th>Location</th>
                  <th>Type</th>
                  <th>Date Posted</th>
                  <th>Deadline</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedJobs.map((job) => (
                  <tr key={job._id}>
                    <td>{job.title}</td>
                    <td>{job.company}</td>
                    <td>{job.location}</td>
                    <td>{job.type}</td>
                    <td>{formatDate(job.datePosted)}</td>
                    <td>{formatDate(job.deadline)}</td>
                    <td className="jobs-actions">
                      <Eye size={16} onClick={() => navigate(`/admin-panel/jobs/view/${job._id}`)} />
                      <Pencil size={16} onClick={() => navigate(`/admin-panel/jobs/edit/${job._id}`)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* PAGINATION */}
            <div className="jobs-pagination">
              <button disabled={page === 1} onClick={() => setPage(page - 1)}>
                Previous
              </button>
              <span>
                Page {page} of {totalPages}
              </span>
              <button disabled={page === totalPages} onClick={() => setPage(page + 1)}>
                Next
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
